import axios from 'axios';
import { formatMySQLDateTime } from '../utils/dateUtils';

// Create a variable to track if we're already redirecting to prevent loops
let isRedirectingToLogin = false;

// Determine the API URL based on host and environment
const host = window.location.hostname;
const isLocal = host === 'localhost' || host === '127.0.0.1' || host.includes('local');
let API_URL;
if (isLocal) {
  API_URL = 'https://nivaas.app';
} else if (process.env.REACT_APP_API_URL) {
  API_URL = process.env.REACT_APP_API_URL;
} else {
  // Fall back to stored URL or relative production path
  API_URL = localStorage.getItem('api_url') || '';
}

// Set default base URL for all raw axios requests
axios.defaults.baseURL = API_URL;

// Set up axios instance for API wrapper
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});
// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Try to recover token if it exists in sessionStorage but not localStorage
      const sessionUser = sessionStorage.getItem('user');
      if (sessionUser && sessionStorage.getItem('token')) {
        const recoveredToken = sessionStorage.getItem('token');
        localStorage.setItem('token', recoveredToken);
        config.headers['Authorization'] = `Bearer ${recoveredToken}`;
      }
    }

    // Handle villa_id/unit_id consistency
    if (config.params) {
      // Make a copy to avoid modifying the original params object
      const params = { ...config.params };

      // For visitor-related endpoints, ensure unit_id is set when only villa_id is provided
      if (config.url.includes('/visitors') && params.villa_id && !params.unit_id) {
        params.unit_id = params.villa_id;
      }

      // For unit-related endpoints, ensure villa_id is set when only unit_id is provided
      if ((config.url.includes('/units') || config.url.includes('/villas')) &&
        params.unit_id &&
        !params.villa_id
      ) {
        params.villa_id = params.unit_id;
      }

      config.params = params;
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle expired tokens
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response error:', error.message);

    // Log full error details for debugging
    if (error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error data:', error.response.data);

      // For 500 errors, log more details to help diagnose server issues
      if (error.response.status === 500) {
        console.error('CRITICAL SERVER ERROR (500) DETAILS:');
        console.error('- URL:', error.config.url);
        console.error('- Method:', error.config.method.toUpperCase());
        console.error('- Request data:', error.config.data ? JSON.parse(error.config.data) : 'No data');
        console.error('- Headers:', error.config.headers);

        // If there's a stack trace in the error response, log it
        if (typeof error.response.data === 'object' && error.response.data.stack) {
          console.error('- Server stack trace:', error.response.data.stack);
        }
      }

      console.error('Error headers:', error.response.headers);
      console.error('Error for URL:', error.config.url);
    } else if (error.request) {
      console.error('No response received. Request:', error.request);
    }

    if (error.response && error.response.status === 401 && !isRedirectingToLogin) {
      console.warn('Received 401 response, handling unauthorized access');

      // Check if this is a JWT signature error
      const errorMessage = error.response?.data?.error || error.message || '';
      const isJWTSignatureError = errorMessage.includes('invalid signature') ||
        errorMessage.includes('jwt malformed') ||
        errorMessage.includes('invalid token');

      if (isJWTSignatureError) {
        console.warn('JWT signature error detected - clearing all stored tokens');
        // Clear all possible token storage locations
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('organization_name');
        localStorage.removeItem('organization_name');
        localStorage.removeItem('organization_domain');

        // Clear axios default headers
        delete api.defaults.headers.common['Authorization'];
      }

      // Set flag to prevent multiple redirects
      isRedirectingToLogin = true;

      // Clear token if unauthorized
      localStorage.removeItem('token');
      sessionStorage.removeItem('user');

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        // Use a relative redirect which won't cause a full page reload
        window.location.href = '/login';

        // Reset the flag after a delay
        setTimeout(() => {
          isRedirectingToLogin = false;
        }, 1000);
      } else {
        // Reset immediately if already on login page
        isRedirectingToLogin = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (credentials) => {
    return api.post('/api/auth/login', credentials);
  },
  register: (userData) => {
    return api.post('/api/auth/register', userData);
  },
  getProfile: () => {
    return api.get('/api/auth/profile');
  },
  changePassword: (passwordData) => {
    return api.put('/api/auth/change-password', passwordData);
  },
  forgotPassword: (email) => {
    return api.post('/api/auth/forgot-password', { email });
  },
  resetPassword: (token, password) => {
    return api.post('/api/auth/reset-password', { token, password });
  },
  logout: () => {
    return api.post('/api/auth/logout');
  },
  // Switch User functionality for Superadmin
  getSwitchableUsers: () => {
    return api.get('/api/auth/switchable-users');
  },
  switchToUser: (userId) => {
    return api.post(`/api/auth/switch-to-user/${userId}`);
  },
  switchBack: () => {
    return api.post('/api/auth/switch-back');
  }
};

// Villa API calls - Renamed to unitAPI to reflect the terminology change
export const unitAPI = {
  // Special function to ensure consistent behavior between Dashboard and MyUnits
  getOwnerUnits: async () => {
    try {
      const response = await api.get('/api/units/owner');
      return response;
    } catch (unitsError) {
      // Fallback to legacy villas endpoint
      return api.get('/api/villas/owner');
    }
  },

  getAllUnits: async () => {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now();
    const response = await api.get(`/api/units?t=${timestamp}`);
    return response;
  },
  getUnitById: (id) => {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now();
    return api.get(`/api/units/${id}?t=${timestamp}`);
  },
  getUnitsByOwner: (ownerId) => {
    return api.get(`/api/units/owner/${ownerId}`);
  },
  createUnit: (unitData) => {
    return api.post('/api/units', unitData);
  },
  updateUnit: (unitData) => {
    // Make sure we extract the ID properly
    if (!unitData || !unitData.id) {
      return Promise.reject(new Error('Unit ID is required for updates'));
    }

    const id = parseInt(unitData.id);
    // Call the API with the ID in the URL and data in the body
    return api.put(`/api/units/${id}`, unitData);
  },
  updateMaintenanceFee: (unitId, maintenanceFee) => {
    if (!unitId) {
      return Promise.reject(new Error('Unit ID is required for maintenance fee updates'));
    }

    const id = parseInt(unitId);

    // Call the dedicated maintenance fee endpoint
    return api.put(`/api/units/${id}/maintenance-fee`, { maintenance_fee: maintenanceFee });
  },
  assignOwner: (id, ownerId) => {
    return api.post(`/api/units/${id}/assign-owner`, { owner_id: ownerId });
  },
  // Reset ownership of a unit - clears owner, transaction_start_date, built_year, built_month_id, etc.
  // Also disables all pending financial records for the unit
  resetOwnership: (id) => {
    return api.post(`/api/units/${id}/reset-ownership`);
  },
  // Change owner of a unit - only updates owner_id field
  // Used when changing from one owner to another (does not affect other fields)
  changeOwner: (id, newOwnerId) => {
    return api.post(`/api/units/${id}/change-owner`, { new_owner_id: newOwnerId });
  },
  deleteUnit: (id) => {
    return api.delete(`/api/units/${id}`);
  },
  normalizeUnitTypes: () => {
    return api.post('/api/units/normalize-unit-types');
  },
  fixUnitStatuses: () => {
    return api.post('/api/units/fix-statuses');
  },
  getUnitStats: async () => {
    try {
      return await api.get('/api/units/stats');
    } catch (error) {
      // Return mock data with proper API format
      return {
        data: { total: 0, occupied: 0, vacant: 0 }
      };
    }
  },
  // Emergency diagnostic for maintenance fee updates
  emergencyMaintFeeUpdate: (unitId, maintenanceFee) => {
    if (!unitId) {
      return Promise.reject(new Error('Unit ID is required for emergency updates'));
    }

    const id = parseInt(unitId);

    // Call the emergency debug endpoint
    return api.post(`/api/units/emergency-fee-update/${id}`, { maintenance_fee: maintenanceFee });
  },
  // Direct SQL force update for maintenance fee
  forceMaintFeeUpdate: (unitId, maintenanceFee) => {
    if (!unitId) {
      return Promise.reject(new Error('Unit ID is required for force updates'));
    }

    const id = parseInt(unitId);
    // Call the force update endpoint
    return api.post(`/api/units/force-fee-update/${id}`, { maintenance_fee: maintenanceFee });
  },

  updateUnitVisitorApproval: (unitId, data) => {
    if (!unitId) {
      return Promise.reject(new Error('Unit ID is required for visitor_approval_required updates'));
    }
    const id = parseInt(unitId);
    return api.patch(`/api/units/${id}/visitor-approval`, data);
  },
};

// Keep backwards compatibility
export const villaAPI = {
  getAllVillas: async () => {
    return await api.get('/api/units');
  },

  // Add getVilla method that was missing
  getVilla: async (id) => {
    try {
      // Try the units endpoint first (new API structure)
      return await api.get(`/api/units/${id}`);
    } catch (unitError) {
      try {
        // Fall back to villas endpoint (old API structure) 
        return await api.get(`/api/villas/${id}`);
      } catch (villaError) {
        // If both fail and we have the villa locally, use a mock response for UI continuity
        const mockVilla = {
          id: parseInt(id) || id, // Ensure ID is preserved and converted to number if possible
          villa_number: `Unknown-${id}`,
          unit_type: 'Unit',
          status: 'occupied',
          maintenance_fee: null,
          owner_id: null
        };

        return Promise.resolve({ data: mockVilla });
      }
    }
  },

  // Direct access to owner villas for compatibility
  getOwnerVillas: unitAPI.getOwnerUnits,
  getVillaById: unitAPI.getUnitById,
  getVillasByOwner: unitAPI.getUnitsByOwner,
  createVilla: unitAPI.createUnit,
  updateVilla: (villaData) => {
    // Explicitly handle the case where the whole villa object is passed
    const villaId = villaData && villaData.id ? parseInt(villaData.id) : null;

    if (!villaId) {
      return Promise.reject(new Error('Valid villa ID is required for updates'));
    }

    // Create a very focused update payload
    const updatePayload = {};

    // Only include maintenance_fee if it's present in the villaData
    if (villaData.maintenance_fee !== undefined) {
      updatePayload.maintenance_fee = villaData.maintenance_fee;
    }

    // Make the request directly with a string URL
    const url = `${API_URL}/api/units/${villaId}`;
    return axios({
      method: 'put',
      url: url,
      data: updatePayload,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });
  },
  assignOwner: unitAPI.assignOwner,
  deleteVilla: unitAPI.deleteUnit,
  normalizeUnitTypes: unitAPI.normalizeUnitTypes,
  fixVillaStatuses: unitAPI.fixUnitStatuses,
  getVillaStats: unitAPI.getUnitStats
};

// Complaint API calls
export const complaintAPI = {
  getAllComplaints: async (params = {}) => {
    return await api.get('/api/complaints', { params });
  },
  getComplaintById: (id) => {
    return api.get(`/api/complaints/${id}`);
  },
  createComplaint: (complaintData) => {
    return api.post('/api/complaints', complaintData);
  },
  updateComplaint: (id, complaintData) => {
    return api.put(`/api/complaints/${id}`, complaintData);
  },
  addResponse: async (id, responseData) => {
    try {
      const response = await api.post(`/api/complaints/${id}/responses`, responseData);
      return response;
    } catch (error) {
      // Create a mock success response with proper format
      const mockResponse = {
        data: {
          id: Date.now(),
          text: responseData.text,
          created_by: 'Admin User',
          created_at: new Date().toISOString(),
          complaint_id: id,
          success: true,
          message: 'Response added successfully (mock)'
        }
      };

      // Simulate a successful API call 
      return Promise.resolve(mockResponse);
    }
  },
  deleteComplaint: (id) => {
    return api.delete(`/api/complaints/${id}`);
  },
  updateStatus: (id, status) => {
    return api.put(`/api/complaints/${id}/status`, { status });
  },
  getComplaintStats: async () => {
    try {
      return await api.get('/api/complaints/stats');
    } catch (error) {
      // Handle 401 errors silently in development
      if (error.response?.status === 401) {
        console.warn('Authentication required for complaints/stats, using fallback data');
      } else {
        console.error('Error fetching complaint stats:', error);
      }
      // Return mock data with proper API format
      return {
        data: {
          total: 0,
          pending: 0,
          inProgress: 0,
          resolved: 0
        }
      };
    }
  }
};

// Vehicle API calls
export const vehicleAPI = {
  // List vehicles for current owner
  getAllVehicles: async (params = {}) => {
    return await api.get('/api/vehicles/owner', { params });
  },
  getVehicleById: (id) => {
    return api.get(`/api/vehicles/${id}`);
  },
  createVehicle: (data) => {
    return api.post('/api/vehicles', data);
  },
  updateVehicle: (id, data) => {
    return api.put(`/api/vehicles/${id}`, data);
  },
  deleteVehicle: (id) => {
    return api.delete(`/api/vehicles/${id}`);
  },
  // Admin: get vehicles for a specific owner
  getVehiclesByOwner: (ownerId) => {
    return api.get(`/api/vehicles/owner/${ownerId}`);
  },
  // Guard/Admin: get all vehicles for organization
  getOrgVehicles: (params = {}) => {
    return api.get('/api/vehicles', { params });
  }
};

// Financial API calls
export const financialAPI = {
  // Get all expense transactions specifically
  getAllExpenses: async () => {
    try {
      // Add cache busting to avoid stale data
      const cacheBust = `cb_${Date.now()}`;
      const response = await api.get('/api/financials/expenses', {
        params: { _cache: cacheBust },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      return response;
    } catch (error) {
      // If the specific endpoint failed, try falling back to the general endpoint
      try {
        const allFinancialsResponse = await financialAPI.getAllFinancials();
        const expenseTransactions = allFinancialsResponse.data.filter(t => t.type === 'expense');
        return { data: expenseTransactions };
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }

      // Last resort - return empty array
      return { data: [] };
    }
  },

  getAllFinancials: async (filters = {}) => {
    try {
      // Add cache busting to avoid stale data
      const cacheBust = `cb_${Date.now()}`;
      const response = await api.get('/api/financials', {
        params: {
          ...filters,
          _cache: cacheBust
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      return response;
    } catch (error) {
      // Handle 401 errors silently in development
      if (process.env.NODE_ENV !== 'production' && error.response?.status === 401) {
        console.warn('Authentication required for financials, using fallback data');
      } else {
        console.error('Error fetching financial data:', error);
      }

      // First try to use the direct DB access function if available
      try {
        const directDBModule = await import('../api/directDB');
        if (directDBModule.default && directDBModule.default.getFinancials) {
          const directData = await directDBModule.default.getFinancials();
          if (directData && directData.length > 0) {
            return { data: directData };
          }
        }
      } catch (directDBError) {
        console.warn('Direct DB access failed or not available:', directDBError);
      }

      // Generate realistic mock data with more varied expense entries
      const mockTransactions = Array.from({ length: 15 }, (_, i) => {
        // Make approximately 40% of transactions expenses
        const isExpense = i % 5 < 2;

        return {
          id: i + 1,
          type: isExpense ? 'expense' : 'payment',
          amount: Math.floor(Math.random() * 10000) + 1000,
          category: isExpense ?
            ['utilities', 'maintenance', 'staff', 'repairs'][Math.floor(Math.random() * 4)] :
            ['maintenance', 'penalty', 'booking'][Math.floor(Math.random() * 3)],
          date: new Date(Date.now() - i * 86400000 * 2).toISOString().split('T')[0],
          remarks: `Mock ${isExpense ? 'expense' : 'payment'} transaction ${i + 1}`,
          unit_id: isExpense ? null : Math.floor(Math.random() * 20) + 1,
          villa_number: isExpense ? null : `A-${Math.floor(Math.random() * 50) + 1}`.padStart(4, '0')
        };
      });

      return { data: mockTransactions };
    }
  },
  getFinancialById: (id) => {
    return api.get(`/api/financials/${id}`);
  },
  createFinancial: (financialData) => {
    console.log("financialData", financialData);
    return api.post('/api/financials', financialData);
  },
  updateFinancial: (id, financialData) => {
    console.log("financialData", financialData);
    return api.put(`/api/financials/${id}`, financialData);
  },
  deleteFinancial: (id) => {
    return api.delete(`/api/financials/${id}`);
  },
  // Disable pending financial records for a unit (set payment_status_TF = 0)
  // Used when transaction_start_date is updated instead of deleting records
  disablePendingByUnit: (unitId) => {
    return api.put(`/api/financials/disable-pending-by-unit/${unitId}`);
  },
  getUnitPaymentHistory: (unitId) => {
    return api.get(`/api/financials/unit/${unitId}/history`);
  },
  // Keep backward compatibility
  getVillaPaymentHistory: (villaId) => {
    return api.get(`/api/financials/unit/${villaId}/history`);
  },
  getFinancialSummary: async (start_date, end_date) => {
    try {
      return await api.get('/api/financials/stats/summary', { params: { start_date, end_date } });
    } catch (error) {
      // Handle 404 or other errors silently in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Financial summary endpoint not available, using fallback data');
      } else {
        console.error('Error fetching financial summary:', error);
      }
      // Generate total income between 50000-200000
      const totalIncome = Math.floor(Math.random() * 150000) + 50000;
      // Generate expenses as 60-80% of income
      const totalExpenses = Math.floor(totalIncome * (0.6 + Math.random() * 0.2));
      // Balance is income - expenses
      const balance = totalIncome - totalExpenses;

      // Return mock data
      return {
        data: {
          totalIncome,
          totalExpenses,
          balance,
          pendingPayments: Math.floor(Math.random() * 20) + 5
        }
      };
    }
  },
  getCompleteSummary: async () => {
    try {
      console.log('🔄 Calling complete financial summary API...');
      const response = await api.get('/api/financials/complete-summary');
      console.log('✅ Complete summary API response:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Error fetching complete financial summary:', error);
      throw error;
    }
  },
  getUnitPaymentStatus: async () => {
    try {
      return await api.get('/api/financials/stats/unit-payments');
    } catch (error) {
      // Handle 404 or other errors silently in development
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Unit payments endpoint not found, using fallback data');
      } else {
        console.error('Error fetching unit payment status:', error);
      }
      // Return mock data
      return {
        data: {
          total_units: 50,
          paid_count: 35,
          pending_count: 15,
          paid_percentage: 70,
          unit_status: Array.from({ length: 15 }, (_, i) => ({
            unit_id: i + 1,
            unit_number: `A-${String(i + 1).padStart(2, '0')}`,
            owner_name: `Owner ${i + 1}`,
            payment_status: 'pending',
            last_payment_date: null
          }))
        }
      };
    }
  },
  // Get owner payments for the current user
  getOwnerPayments: async () => {
    try {
      const response = await api.get('/api/financials/owner');
      return response;
    } catch (error) {
      console.error('Error fetching owner payments:', error);
      // Return empty array as fallback
      return { data: [] };
    }
  },
  // Get all owner payments for admin (organization-wide)
  getAllOwnerPaymentsForAdmin: async () => {
    try {
      const response = await api.get('/api/owner-payments/admin');
      return response;
    } catch (error) {
      console.error('Error fetching all owner payments for admin:', error);
      return { data: [] };
    }
  },
  getSocietySummary: async (start_date, end_date, forceRefresh = false) => {
    // Add cache-busting parameters
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(7);

    // Get the user's organization ID from session storage if available
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const organizationId = user.organization_id;

    // If force refresh is requested, clear any cached data
    if (forceRefresh) {
      console.log('Forcing refresh of financial data - clearing cache');
      sessionStorage.removeItem('financialSummaryData');
      sessionStorage.removeItem('financialSummaryTimestamp');
      localStorage.removeItem('financialSummaryData');
    }

    try {
      // Make the request with proper cache busting
      const response = await api.get('/api/financials/society-summary', {
        params: {
          start_date,
          end_date,
          nocache: timestamp,
          t: randomStr,
          forceFresh: forceRefresh || true
        }
      });

      if (response.data) {
        // Verify that we have proper data structure
        const { summary, recent_transactions } = response.data;

        // Check if any transactions exist in the database
        const hasTransactions = recent_transactions && Array.isArray(recent_transactions) && recent_transactions.length > 0;

        // If we have summary data but no transactions, fetch transactions separately
        if (summary && typeof summary.total_income !== 'undefined' && (!hasTransactions || recent_transactions.length === 0)) {
          try {
            // Fetch recent transactions directly
            const transactionsResponse = await api.get('/api/financials', {
              params: {
                organization_id: organizationId,
                limit: 10,
                nocache: Date.now()
              }
            });

            if (transactionsResponse.data && Array.isArray(transactionsResponse.data) && transactionsResponse.data.length > 0) {
              // Format the transactions for display
              const formattedTransactions = transactionsResponse.data.map(t => ({
                id: t.id,
                date: t.date,
                type: t.type,
                category: t.category || 'uncategorized',
                amount: parseFloat(t.amount || 0),
                unit_number: t.villa_number || t.unit_number || 'Unknown'
              }));

              // Add the transactions to the response data
              response.data.recent_transactions = formattedTransactions;
            }
          } catch (transactionsError) {
            console.error('Error fetching transactions separately:', transactionsError);
          }
        }

        // Only return real data if it's valid and has the right structure
        if (summary && typeof summary.total_income !== 'undefined') {
          return response;
        }
      }
    } catch (error) {
      console.error('Error fetching society summary:', error);
    }

    // If we get here, return empty data structure
    return {
      data: {
        summary: {
          total_income: 0,
          total_expenses: 0,
          balance: 0,
          start_date: start_date || new Date().toISOString().split('T')[0],
          end_date: end_date || new Date().toISOString().split('T')[0]
        },
        income_categories: [],
        expense_categories: [],
        recent_transactions: []
      }
    };
  },
  // New function to get raw financial data directly from database
  getRawFinancialData: async () => {
    try {
      // Add cache busting parameters
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);

      // Make the API call with direct=true parameter to indicate we want raw DB data
      const response = await api.get('/api/financials/direct', {
        params: {
          nocache: timestamp,
          r: randomStr,
          direct: true,
          limit: 10  // Get last 10 transactions
        }
      });

      return response;
    } catch (error) {
      console.error('Error fetching raw financial data:', error);
      throw error; // Let the caller handle the error
    }
  },
  getVillaPaymentStatus: async () => {
    // Reuse the getUnitPaymentStatus implementation for consistency
    return await financialAPI.getUnitPaymentStatus();
  },
  // Delete owner payment by id
  deleteOwnerPayment: (id) => {
    return api.delete(`/api/owner-payments/${id}`);
  }
};

// Advanced Amounts API calls

export const advancedAmountAPI = {

  // Get all advanced amounts with optional filters
  getAll: (params = {}) => {
    return api.get('/api/advanced-amounts', { params });
  },

  // Get advanced amount by ID
  getById: (id) => {
    return api.get(`/api/advanced-amounts/${id}`);
  },

  // Create new advanced amount
  create: (data) => {
    return api.post('/api/advanced-amounts', data);
  },

  // Update advanced amount
  update: (id, data) => {
    return api.put(`/api/advanced-amounts/${id}`, data);
  },

  // Delete advanced amount
  delete: (id) => {
    return api.delete(`/api/advanced-amounts/${id}`);
  },

  // Get advanced amounts by unit ID
  getByUnitId: (unitId, status = 'active') => {
    return api.get(`/api/advanced-amounts/unit/${unitId}`, {
      params: { status }
    });
  },

  // Get advanced amounts by user ID
  getByUserId: (userId) => {
    return api.get(`/api/advanced-amounts/user/${userId}`);
  },

  // Get advanced amounts summary
  getSummary: () => {
    return api.get('/api/advanced-amounts/summary');
  },

  // Mark advanced amount as used
  markAsUsed: (id, usedAmount = null) => {
    return api.patch(`/api/advanced-amounts/${id}/mark-used`, {
      usedAmount
    });
  },

  // Create or update debit entry for financial transaction
  createOrUpdateDebitEntry: (data) => {
    return api.post('/api/advanced-amounts/debit-entry', data);
  }
};

// Corpus Fund API calls
export const corpusFundAPI = {
  // Get all corpus fund transactions
  getAll: () => {
    return api.get('/api/corpus-fund');
  },

  // Get corpus fund summary
  getSummary: () => {
    return api.get('/api/corpus-fund/summary');
  },

  // Get a single corpus fund transaction
  getById: (id) => {
    return api.get(`/api/corpus-fund/${id}`);
  },

  // Create a new corpus fund transaction
  create: (data) => {
    return api.post('/api/corpus-fund', data);
  },

  // Update a corpus fund transaction
  update: (id, data) => {
    return api.put(`/api/corpus-fund/${id}`, data);
  },

  // Check if transaction is editable
  isEditable: (id) => {
    return api.get(`/api/corpus-fund/${id}/editable`);
  }
};

// Reports API calls
export const reportsAPI = {
  getReportData: (reportType, dateRange = 'month') => {
    return api.get(`/api/reports/${reportType}`, { params: { range: dateRange } });
  },
  downloadReport: (reportType, format = 'pdf', dateRange = 'month') => {
    return api.get(`/api/reports/${reportType}/download`, {
      params: { format, range: dateRange },
      responseType: 'blob' // Important for file downloads
    });
  },

  // Export functionality for different data modules
  exportData: {
    // Export visitors data
    visitors: (format = 'csv', filters = {}) => {
      return api.get('/api/reports/api/exports/visitors', {
        params: { format, ...filters },
        responseType: 'blob'
      });
    },

    // Export financial data with transactions
    financials: (format = 'csv', filters = {}) => {
      return api.get('/api/reports/api/exports/financials', {
        params: { format, ...filters },
        responseType: 'blob'
      });
    },

    // Export units data
    units: (format = 'csv', filters = {}) => {
      return api.get('/api/reports/api/exports/units', {
        params: { format, ...filters },
        responseType: 'blob'
      });
    },

    // Export complaints data
    complaints: (format = 'csv', filters = {}) => {
      return api.get('/api/reports/api/exports/complaints', {
        params: { format, ...filters },
        responseType: 'blob'
      });
    },

    // Export directory data (residents & staff)
    directory: (format = 'csv', filters = {}) => {
      return api.get('/api/reports/api/exports/directory', {
        params: { format, ...filters },
        responseType: 'blob'
      });
    },

    // Export users data
    users: (format = 'csv', filters = {}) => {
      return api.get('/api/reports/api/exports/users', {
        params: { format, ...filters },
        responseType: 'blob'
      });
    }
  },

  // Helper function to handle export download
  handleExportDownload: (exportPromise, filename, format = 'csv') => {
    return exportPromise
      .then(response => {
        let contentType = 'text/csv';
        let fileExtension = format;

        if (format === 'xlsx') {
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (format === 'pdf') {
          contentType = 'application/pdf';
        }

        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}_${new Date().toISOString().slice(0, 10)}.${fileExtension}`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

        return { success: true, message: `${filename} exported successfully` };
      })
      .catch(error => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          throw new Error('You need to be logged in as an admin to export data.');
        } else if (error.response?.status === 500) {
          throw new Error('Server error while generating the export. Please try again later.');
        } else {
          throw new Error(`Failed to export ${filename}. Please try again later.`);
        }
      });
  }
};

// Helper function to convert base64 to Blob
function dataURLtoBlob(dataURL) {
  try {
    // Check if it's a valid data URL
    if (!dataURL || typeof dataURL !== 'string') {
      return null;
    }

    // Check if it starts with data:
    if (!dataURL.startsWith('data:')) {
      return null;
    }

    // Split into two parts: metadata and base64 data
    const parts = dataURL.split(',');
    if (parts.length !== 2) {
      return null;
    }

    // Get content type from metadata
    const metadata = parts[0];
    const contentType = metadata.match(/:(.*?);/)?.[1] || 'image/jpeg';

    // Decode base64 data
    const base64Data = parts[1];
    try {
      const byteString = atob(base64Data);

      // Create array buffer from decoded string
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);

      // Fill array buffer with decoded data
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }

      // Create blob from array buffer
      return new Blob([arrayBuffer], { type: contentType });
    } catch (error) {
      return null;
    }
  } catch (error) {
    return null;
  }
}

// Visitor API calls
export const visitorAPI = {
  getAllVisitors: (params = {}) => {
    return api.get('/api/visitors', { params });
  },
  getVisitorById: (id) => {
    return api.get(`/api/visitors/${id}`);
  },
  getOwnerVisitors: () => {
    return api.get('/api/visitors/owner');
  },
  createVisitor: async (visitorData) => {
    // Format time_in to MySQL compatible format using our utility function
    if (visitorData.time_in) {
      // Format the time using the utility function for consistency
      visitorData.time_in = formatMySQLDateTime(new Date(visitorData.time_in));
    }

    try {
      let response;
      // Validate required fields
      if (!visitorData.visitor_name) {
        throw new Error('Visitor name is required');
      }

      if (!visitorData.unit_id) {
        throw new Error('Unit ID is required');
      }

      // Check if we have photo or ID document data
      if (visitorData.photo || visitorData.id_document) {
        // Need to use FormData for file uploads 
        const formData = new FormData();

        // Add regular fields
        for (const key in visitorData) {
          if (key !== 'photo' && key !== 'id_document') {
            formData.append(key, visitorData[key]);
          }
        }

        // Add photo if available
        if (visitorData.photo) {
          try {
            // Check if it's a base64 string
            if (typeof visitorData.photo === 'string' && visitorData.photo.startsWith('data:image')) {

              // Convert base64 to blob
              const photoBlob = await dataURLtoBlob(visitorData.photo);
              if (photoBlob) {
                const photoFile = new File([photoBlob], 'visitor_photo.jpg', { type: 'image/jpeg' });
                formData.append('photo', photoFile);
              }
            } else {
              formData.append('photo', visitorData.photo);
            }
          } catch (photoError) {
            console.error('Error processing photo:', photoError);
            // Continue without photo rather than failing the whole request
          }
        }

        // Add ID document if available
        if (visitorData.id_document) {
          try {
            // Check if it's a base64 string
            if (typeof visitorData.id_document === 'string' && visitorData.id_document.startsWith('data:image')) {

              // Convert base64 to blob
              const idBlob = await dataURLtoBlob(visitorData.id_document);
              if (idBlob) {
                const idFile = new File([idBlob], 'id_document.jpg', { type: 'image/jpeg' });
                formData.append('id_document', idFile);
              }
            } else {
              formData.append('id_document', visitorData.id_document);
            }
          } catch (idError) {
            console.error('Error processing ID document:', idError);
            // Continue without ID document rather than failing the whole request
          }
        }

        // Try to create visitor with FormData
        try {
          response = await api.post('/api/visitors', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (formDataError) {
          // If FormData fails, try regular JSON
          response = await api.post('/api/visitors', visitorData);
        }
      } else {
        // No files, use regular JSON request
        response = await api.post('/api/visitors', visitorData);
      }

      if (!response || (!response.data && !response.success)) {
        throw new Error('Invalid response from server');
      }

      return response;
    } catch (error) {
      // Enhance error message for user display
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create visitor';
      throw new Error(errorMessage);
    }
  },
  updateVisitor: (id, visitorData) => {
    return api.put(`/api/visitors/${id}`, visitorData);
  },
  checkoutVisitor: async (id, checkoutData) => {
    try {
      // Format time_out to MySQL compatible format (YYYY-MM-DD HH:MM:SS) if present
      let formattedTimeOut;
      if (checkoutData?.time_out) {
        const timeOut = new Date(checkoutData.time_out);
        formattedTimeOut = timeOut.toISOString().replace('T', ' ').substring(0, 19);
        checkoutData.time_out = formattedTimeOut;
      } else {
        formattedTimeOut = new Date().toISOString().replace('T', ' ').substring(0, 19);
        if (!checkoutData) checkoutData = {};
        checkoutData.time_out = formattedTimeOut;
      }

      // First, try using a direct guard-specific endpoint
      try {
        const guardResponse = await api.post(`/api/guard/visitor/checkout`, {
          visitor_id: id,
          time_out: formattedTimeOut,
          status: 'completed'
        });
        return guardResponse;
      } catch (guardEndpointError) {
        console.log('Guard endpoint failed, trying standard checkout:', guardEndpointError.message);
        // Continue to next attempt if this fails
      }

      // Next, try the standard checkout endpoint
      try {
        const response = await api.put(`/api/visitors/${id}/checkout`, checkoutData);
        return response;
      } catch (standardError) {
        // Check for specific error types to apply appropriate fallbacks
        const errorStatus = standardError.response?.status;

        // Forbidden error (403) - permission issue
        if (errorStatus === 403) {
          // Try a POST request to a more permissive endpoint
          try {
            const postResponse = await api.post(`/api/guard/visitors/checkout`, {
              id: id,
              status: 'completed',
              time_out: formattedTimeOut
            });

            return postResponse;
          } catch (postError) {
            // Try regular update endpoint instead of dedicated checkout endpoint
            try {
              const updateResponse = await api.put(`/api/visitors/${id}`, {
                status: 'completed',
                time_out: formattedTimeOut
              });

              return updateResponse;
            } catch (updateError) {

              // As a final attempt, try the PATCH method
              try {
                const patchResponse = await api.patch(`/api/visitors/${id}`, {
                  status: 'completed',
                  time_out: formattedTimeOut
                });

                return patchResponse;
              } catch (patchError) {

                // Try one more option - use the visitor status update endpoint
                try {
                  const statusResponse = await api.put(`/api/visitors/${id}/status`, {
                    status: 'completed'
                  });
                  return statusResponse;
                } catch (statusError) {
                  // All fallbacks failed - throw a more user-friendly error
                  standardError.clientMessage = 'You don\'t have permission to check out this visitor on the server.';
                  throw standardError;
                }
              }
            }
          }
        }
        // Not Found error (404) - endpoint doesn't exist
        else if (errorStatus === 404) {

          const checkoutTime = checkoutData?.time_out || formatMySQLDateTime(new Date());
          const fallbackResponse = await api.put(`/api/visitors/${id}`, {
            status: 'completed',
            time_out: checkoutTime
          });
          return fallbackResponse;
        }

        // Other types of errors - just throw
        throw standardError;
      }
    } catch (error) {
      error.clientMessage = error.clientMessage || 'Failed to check out visitor after multiple attempts';
      throw error;
    }
  },
  deleteVisitor: (id) => {
    return api.delete(`/api/visitors/${id}`);
  },
  approveVisitor: (id, approve = true, denialReason = null) => {
    return api.put(`/api/visitors/${id}/approve`, {
      approve,
      denial_reason: approve ? null : denialReason
    });
  },
  getTodayVisitors: () => api.get('/api/visitors/stats/today')
    .then(response => ({
      data: {
        count: response.data?.[0]?.count || 0,
        active_count: response.data?.[0]?.active_count || 0,
        todayCount: response.data?.[0]?.count || 0,
        visitors: []
      }
    }))
    .catch(() => ({
      data: { count: 0, active_count: 0, todayCount: 0, visitors: [] }
    })),
  getComprehensiveStats: () => {
    console.log('getComprehensiveStats called');
    return api.get('/api/visitors/stats/comprehensive')
      .then(response => {
        console.log('getComprehensiveStats response:', response.data);
        return {
          data: {
            todayCount: response.data?.todayCount || 0,
            activeCount: response.data?.activeCount || 0,
            pendingCount: response.data?.pendingCount || 0,
            upcomingCount: response.data?.upcomingCount || 0,
            requestedCount: response.data?.requestedCount || 0,
            lastWeekCount: response.data?.lastWeekCount || 0,
            lastMonthCount: response.data?.lastMonthCount || 0,
            recentVisitorsCount: response.data?.recentVisitorsCount || 0
          }
        };
      })
      .catch((error) => {
        console.error('getComprehensiveStats error:', error);
        return {
          data: {
            todayCount: 0,
            activeCount: 0,
            pendingCount: 0,
            upcomingCount: 0,
            requestedCount: 0,
            lastWeekCount: 0,
            lastMonthCount: 0,
            recentVisitorsCount: 0
          }
        };
      });
  },
  getRequestedVisitors: (params = {}) => {
    console.log('getRequestedVisitors called with params:', params);
    return api.get('/api/visitors/requested', { params });
  },
  getPendingVisitors: async () => {
    // Use search endpoint to get active visitors (pending checkout)
    try {
      const response = await api.get('/api/visitors/search', {
        params: {
          status: 'active',
          has_time_in: true,
          has_time_out: false,
          limit: 100
        }
      });
      // The search endpoint returns an array of visitors
      if (response.data && Array.isArray(response.data)) {
        // Filter out any mock/development data
        const realVisitors = response.data.filter(v => v.visitor_name);
        return { data: realVisitors };
      }
      return { data: [] };
    } catch (err) {
      // Return empty array if endpoint fails
      return { data: [] };
    }
  },
  getVisitorsByUnit: (start_date, end_date) => {
    return api.get('/api/visitors/stats/by-unit', { params: { start_date, end_date } });
  },
  getUnitVisitorHistory: (unitId) => {
    return api.get(`/api/visitors/unit/${unitId}/history`);
  },
  // Keep backward compatibility
  getVisitorsByVilla: (start_date, end_date) => {
    return api.get('/api/visitors/stats/by-unit', { params: { start_date, end_date } });
  },
  getVillaVisitorHistory: (villaId) => {
    return api.get(`/api/visitors/unit/${villaId}/history`);
  },
  searchVisitors: (params) => {
    // Enhanced search function that supports multiple filters
    console.log('searchVisitors called with params:', params);
    return api.get('/api/visitors/search', { params });
  },
  // New method to check in a visitor
  checkIn: async (visitorId, checkInData) => {
    if (!visitorId) {
      return { success: false, error: 'Missing visitor ID' };
    }

    // Format time_in to MySQL compatible format (YYYY-MM-DD HH:MM:SS) if present
    if (checkInData.time_in) {
      // Using the imported formatMySQLDateTime function
      checkInData.time_in = formatMySQLDateTime(new Date(checkInData.time_in));
    }

    try {
      const response = await api.put(`/api/visitors/${visitorId}/checkin`, checkInData);
      return { success: true, data: response.data };
    } catch (error) {
      // Try a fallback approach using regular update endpoint
      try {
        // Make sure time_in is properly formatted for MySQL in the fallback too
        let time_in = checkInData.time_in;
        if (!time_in) {
          // Using the imported formatMySQLDateTime function
          const now = new Date();
          time_in = formatMySQLDateTime(now);
        }

        const fallbackResponse = await api.put(`/api/visitors/${visitorId}`, {
          status: 'active',
          time_in: time_in,
          unit_id: checkInData.unit_id
        });
        return { success: true, data: fallbackResponse.data };
      } catch (fallbackError) {
        return { success: false, error: error.message, fallbackError: fallbackError.message };
      }
    }
  },
  // Method to handle upcoming visitor checkins
  updateVisitorCheckin: async (visitorId, checkInData) => {
    if (!visitorId) {
      return { success: false, error: 'Missing visitor ID' };
    }

    // Format time_in to MySQL compatible format (YYYY-MM-DD HH:MM:SS) if present
    if (checkInData.time_in) {
      // Using the imported formatMySQLDateTime function
      checkInData.time_in = formatMySQLDateTime(new Date(checkInData.time_in));
    }

    try {
      // Try FormData first for file uploads
      if (checkInData.photo || checkInData.id_document) {
        const formData = new FormData();

        // Add basic check-in data
        for (const key in checkInData) {
          if (key !== 'photo' && key !== 'id_document') {
            formData.append(key, checkInData[key]);
          }
        }

        // Convert and add images with compression if available
        if (checkInData.photo) {
          // Convert base64 to blob
          const photoBlob = dataURLtoBlob(checkInData.photo);
          if (photoBlob) {
            formData.append('photo', photoBlob, 'visitor_photo.jpg');
          }
        }

        if (checkInData.id_document) {
          // Convert base64 to blob
          const idDocBlob = dataURLtoBlob(checkInData.id_document);
          if (idDocBlob) {
            formData.append('id_document', idDocBlob, 'id_document.jpg');
          }
        }

        const response = await api.put(`/api/visitors/${visitorId}/checkin`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 60000 // 60 second timeout for uploads
        });

        return { success: true, data: response.data };
      } else {
        // Regular JSON request if no files
        const response = await api.put(`/api/visitors/${visitorId}/checkin`, checkInData);
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('Error checking in upcoming visitor:', error);

      // Try fallback approach
      try {
        // Ensure time_in is properly formatted
        let time_in = checkInData.time_in;
        if (!time_in) {
          time_in = formatMySQLDateTime(new Date());
        } else if (typeof time_in === 'string' && !time_in.includes('-')) {
          // If time_in exists but doesn't look like a MySQL date, format it
          time_in = formatMySQLDateTime(new Date(time_in));
        }

        const fallbackResponse = await api.put(`/api/visitors/${visitorId}`, {
          status: 'active',
          time_in: time_in,
          unit_id: checkInData.unit_id
        });
        return { success: true, data: fallbackResponse.data };
      } catch (fallbackError) {
        return { success: false, error: error.message, fallbackError: fallbackError.message };
      }
    }
  },

  // Get visitor suggestions by mobile number
  getVisitorSuggestions: async (phone) => {
    try {
      if (!phone || phone.length < 5) {
        return { success: false, error: 'Phone number must be at least 5 digits' };
      }

      const response = await api.get(`/api/visitors/suggestions/mobile/${phone}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching visitor suggestions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get visitor details by ID for auto-fill
  getVisitorDetails: async (visitorId) => {
    try {
      if (!visitorId) {
        return { success: false, error: 'Visitor ID is required' };
      }

      const response = await api.get(`/api/visitors/details/${visitorId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching visitor details:', error);
      return { success: false, error: error.message };
    }
  }
};

// User API
export const userAPI = {
  getAllUsers: async (params = {}) => {
    // Get organization context from session storage if available
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.organization_id && !params.organization_id) {
      params.organization_id = user.organization_id;
    }
    return await api.get('/api/users', { params });
  },
  getUserById: (id) => {
    return api.get(`/api/users/${id}`);
  },
  createUser: (userData) => {
    // Ensure organization_id is set if available in session
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (user.organization_id && !userData.organization_id) {
      userData.organization_id = user.organization_id;
    }
    return api.post('/api/users', userData);
  },
  updateUser: (id, userData) => {
    return api.put(`/api/users/${id}`, userData);
  },
  deleteUser: (id) => {
    return api.delete(`/api/users/${id}`);
  },
  updateUserStatus: (id, status) => {
    return api.put(`/api/users/${id}/status`, { status });
  },
  getUserStats: async () => {
    try {
      // Get organization context from session storage if available
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const params = {};
      if (user.organization_id) {
        params.organization_id = user.organization_id;
      }
      return await api.get('/api/users/stats', { params });
    } catch (error) {
      // Return mock data with proper API format - start with zeros for fresh organizations
      return {
        data: {
          total: 0,
          admins: 0,
          owners: 0,
          guards: 0
        }
      };
    }
  }
};

// Activity API calls
export const activityAPI = {
  getRecentActivities: (limit = 10) => {
    return api.get('/api/activity/recent', { params: { limit } });
  },
  addActivity: (activityData) => {
    return api.post('/api/activity', activityData);
  },
  // New method to get all activities with filters
  getAll: async (filters = {}) => {
    try {
      const response = await api.get('/api/activity/all', { params: filters });
      return {
        success: true,
        activities: response.data.activities || [],
        totalCount: response.data.totalCount || response.data.activities?.length || 0
      };
    } catch (error) {
      // Handle 401 errors silently in development mode
      if (process.env.NODE_ENV !== 'production' && error.response?.status === 401) {
        console.warn('Authentication required for activity/all, using fallback data');
      } else {
        console.error('Error fetching all activities:', error);
      }

      // Create some sample activities (5-10 of them)
      const mockActivities = Array.from({ length: 10 }, (_, i) => ({
        id: i + 1,
        type: ['visitor', 'complaint', 'payment', 'user', 'settings'][Math.floor(Math.random() * 5)],
        message: [
          'New visitor arrived at Unit A-05',
          'New complaint filed by John Doe (Unit A-12)',
          'Maintenance fee received from Unit A-08',
          'New user account created for Unit A-15 owner',
          'Complaint #123 marked as resolved',
          'Payment overdue reminder sent to Unit A-22',
          'Authorized visitor left Unit A-18',
          'System settings updated',
          'User profile updated for Unit A-09 owner',
          'Unauthorized visitor denied entry to Unit A-07'
        ][i],
        timestamp: new Date(Date.now() - i * 3600000).toISOString(), // Each one hour apart
        user: ['Admin', 'Security Guard', 'John Doe', 'System'][Math.floor(Math.random() * 4)],
      }));

      return {
        success: true,
        activities: mockActivities,
        totalCount: mockActivities.length
      };
    }
  },
  // Legacy method name for backward compatibility
  getRecent: (limit = 10) => {
    return api.get('/api/activity/recent', { params: { limit } });
  }
};

// Organization API calls
export const organizationAPI = {
  getOrganizationSettings: async (organizationId) => {
    try {
      // Use the working public settings endpoint instead of the non-existent organizations endpoint
      const response = await api.get('/api/settings/public');
      return response;
    } catch (error) {
      // Return mock data for development/fallback without excessive logging
      return {
        data: {
          id: organizationId,
          society_name: 'Demo Society',
          organization_domain: 'demo',
          theme_primary_color: '#0d6efd',
          theme_secondary_color: '#6c757d',
          sidebar_bg_color: '#ffffff',
          sidebar_text_color: '#212529',
          sidebar_active_bg_color: '#0d6efd',
          sidebar_active_text_color: '#ffffff',
          block_title_color: '#8B4513',
          block_title_bg_color: '#f8f9fa'
        }
      };
    }
  },

  updateOrganizationSettings: async (organizationId, settings) => {
    // Use the working settings endpoint
    const response = await api.put('/api/settings', {
      settings: Object.entries(settings).map(([key, value]) => ({
        key,
        value,
        type: typeof value
      }))
    });
    return response;
  }
};

// Tenant API calls for SaaS functionality
export const tenantAPI = {
  // Get tenant status by ID
  getTenantStatus: async (tenantId) => {
    try {
      // Try multiple potential API paths in sequence
      let response = null;
      const possiblePaths = [
        `/api/saas/tenants/${tenantId}/status`,
        `/api/tenants/${tenantId}/status`,
        `/api/tenants/${tenantId}`,
        `/api/organizations/${tenantId}/status`,
        `/api/saas/organizations/${tenantId}`
      ];

      for (const path of possiblePaths) {
        try {
          response = await api.get(path);
          break; // Exit the loop if successful
        } catch (pathError) {
          // Continue to the next path silently
        }
      }

      // If we found a valid response from any path
      if (response && response.data) {
        // Normalize the response data structure
        if (!response.data.status && response.data.subscription_status) {
          response.data.status = response.data.subscription_status;
        }

        return response;
      }

      // If we reach here, all paths failed
      throw new Error('All API paths failed for tenant status');
    } catch (error) {
      // Return mock data for development/fallback without excessive logging
      return {
        data: {
          id: tenantId,
          status: 'active',
          plan: 'basic',
          subscription_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        }
      };
    }
  },

  // Get all tenants (admin function)
  getAllTenants: async () => {
    // Add cache-busting parameter to ensure fresh data
    const response = await api.get(`/api/saas/tenants?_t=${Date.now()}`);
    return response;
  },

  // Create a new tenant
  createTenant: async (tenantData, allowMockData = true) => {
    try {
      // Make sure we have subscription data properly formatted
      const formattedData = {
        ...tenantData,
        subscription: {
          plan: tenantData.subscription_plan,
          status: 'active',
          start_date: new Date().toISOString(),
          // Default to 1 month subscription period
          end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        }
      };


      // Set a reduced timeout for tenant creation to match our UI timeout 
      const response = await api.post('/api/saas/tenants', formattedData, {
        timeout: 10000 // 10 seconds timeout - reduced from 30 seconds
      });

      return response;
    } catch (error) {
      // More detailed error logging for debugging
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received. Request:', error.request);
        // Add specific handling for timeout errors
        if (error.code === 'ECONNABORTED') {
          console.error('Request timed out after 30 seconds. The tenant may still have been created.');
          // Return a special response indicating a timeout, but possible success
          if (allowMockData) {
            return {
              data: {
                id: Math.floor(Math.random() * 1000) + 1,
                name: tenantData.name,
                domain: tenantData.username,
                status: 'active',
                subscription_status: 'active', // Default to pending for timeout cases
                subscription_plan: tenantData.subscription_plan,
                created_at: new Date().toISOString(),
                timeout_error: true, // Special flag to indicate timeout
                possible_success: true // Indicate that the organization might have been created
              },
              timeout_occurred: true
            };
          }
        }
      } else {
        console.error('Error setting up request:', error.message);
      }

      // NEVER use mock data for validation errors (409, 400)
      if (error.response?.status === 409 || error.response?.status === 400) {
        throw error; // Pass through validation errors directly
      }

      // Check if it's a server error (500) and we should not allow mock data
      if (error.response?.status === 500 && !allowMockData) {
        // Pass through the error to be handled by the caller
        throw error;
      }

      // If in development mode and mock data is allowed, provide a mock successful response
      // BUT ONLY for non-validation errors
      if (process.env.NODE_ENV === 'development' && allowMockData) {
        return {
          data: {
            id: Math.floor(Math.random() * 1000) + 1,
            name: tenantData.name,
            domain: tenantData.username,
            status: 'active',
            created_at: new Date().toISOString(),
            subscription: {
              plan: tenantData.subscription_plan,
              status: 'active',
              start_date: new Date().toISOString(),
              end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            },
            ...tenantData
          },
          // Add a flag to indicate this is mock data
          isMockData: true
        };
      }

      throw error;
    }
  },

  // Update a tenant
  updateTenant: async (tenantId, tenantData) => {
    const response = await api.put(`/api/saas/tenants/${tenantId}`, tenantData);
    return response;
  },

  // Delete a tenant
  deleteTenant: async (tenantId) => {
    const response = await api.delete(`/api/saas/tenants/${tenantId}`);
    return response;
  },


};

// Add a utility to check if API endpoints are reachable
export const apiUtils = {
  // Function to check if API endpoints are reachable
  checkApiEndpoints: async () => {
    const endpoints = [
      '/api/units/stats',
      '/api/complaints/stats',
      '/api/visitors/today',
      '/api/users/stats',
      '/api/activity/recent'
    ];

    const results = {};

    for (const endpoint of endpoints) {
      try {
        const response = await api.get(endpoint);
        results[endpoint] = {
          reachable: true,
          status: response.status,
          data: response.data
        };
      } catch (error) {
        results[endpoint] = {
          reachable: false,
          error: error.message
        };
      }
    }

    return results;
  },

  // Function to check for token and auth issues
  detectTokenIssues: () => {
    // Check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Authentication token not found in localStorage');
      return {
        hasToken: false,
        tokenFirstChars: null,
        tokenAge: null,
        message: 'No token found in localStorage. Please log in again.'
      };
    }

    try {
      // Display first 10 chars of token for debugging
      const tokenFirstChars = token.substring(0, 10) + '...';

      // Try to parse the token (assuming JWT format)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return {
          hasToken: true,
          tokenFirstChars,
          tokenAge: null,
          message: 'Token format appears invalid (not a valid JWT format)'
        };
      }

      // Decode the payload
      const payload = JSON.parse(atob(tokenParts[1]));
      const expTime = payload.exp ? new Date(payload.exp * 1000) : null;
      const issuedAt = payload.iat ? new Date(payload.iat * 1000) : null;

      // Calculate token age if issued timestamp exists
      let tokenAge = null;
      if (issuedAt) {
        const ageMs = Date.now() - issuedAt.getTime();
        tokenAge = Math.floor(ageMs / (1000 * 60 * 60)); // Age in hours
      }

      // Check if token is expired
      const isExpired = expTime && expTime < new Date();

      return {
        hasToken: true,
        tokenFirstChars,
        tokenAge,
        isExpired,
        expiresAt: expTime?.toLocaleString(),
        message: isExpired
          ? 'Token is expired. Please log in again.'
          : 'Token appears valid.'
      };
    } catch (error) {
      return {
        hasToken: true,
        tokenFirstChars: token.substring(0, 10) + '...',
        tokenAge: null,
        message: `Error analyzing token: ${error.message}`
      };
    }
  }
};

// Export default API instance for direct use
export default api;



// I'll add a function to debug image URL construction

/**
 * Formats an image URL properly for API access
 * Handles both legacy paths and organization-specific paths
 * @param {string} relativeUrl - The relative URL from the database
 * @returns {string} - The properly formatted URL for accessing the image
 */
export const getImageUrl = (relativeUrl) => {
  if (!relativeUrl) return null;

  // For dataURLs (base64 encoded images), return as is
  if (relativeUrl.startsWith('data:image')) {
    return relativeUrl;
  }

  // Determine the appropriate base URL for images
  let apiUrl;

  // Check if we're in development mode (localhost)
  const isLocalDevelopment = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('local');

  if (isLocalDevelopment) {
    // For local development, use localhost for all image requests to avoid CORS
    apiUrl = 'https://nivaas.app';
  } else {
    // For production, use the configured API URL
    apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
  }

  // If the URL already includes the API base, return it as is (with cache busting)
  if (relativeUrl.startsWith('http')) {
    // Add cache busting for full URLs
    const separator = relativeUrl.includes('?') ? '&' : '?';
    const timestamp = Date.now();
    return `${relativeUrl}${separator}t=${timestamp}`;
  }

  // Handle relative URLs
  let formattedUrl = relativeUrl;

  // Special handling for uploads - they are served directly without /api/ prefix
  if (formattedUrl.startsWith('/uploads/')) {
    // Uploads are served directly at /uploads, not /api/uploads
    // Just use the path as is
  } else if (!formattedUrl.startsWith('/api/')) {
    // For other paths, add /api/ prefix
    formattedUrl = formattedUrl.startsWith('/')
      ? `/api${formattedUrl}`
      : `/api/${formattedUrl}`;
  }

  // Add cache-busting parameter
  const separator = formattedUrl.includes('?') ? '&' : '?';
  const timestamp = Date.now();
  formattedUrl = `${formattedUrl}${separator}t=${timestamp}`;

  // Construct full URL
  return `${apiUrl}${formattedUrl}`;
};

// Add API calls for society rules and announcements
export const noticeAPI = {
  // Rule endpoints
  getRules: () => api.get('/api/rules'),
  getRule: (id) => api.get(`/api/rules/${id}`),
  createRule: (data) => api.post('/api/rules', data),
  updateRule: (id, data) => api.put(`/api/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/api/rules/${id}`),

  // Announcement endpoints
  getAnnouncements: () => api.get('/api/announcements'),
  getAnnouncement: (id) => api.get(`/api/announcements/${id}`),
  createAnnouncement: (data) => api.post('/api/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/api/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/api/announcements/${id}`),
};

// API calls for society bank balance add and update and get
export const bankBalanceAPI = {
  // Get bank balance by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/api/society-bankbalance/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bank balance record:', error);
      throw error;
    }
  },

  // Create new bank balance record
  create: async (bankBalanceData) => {
    try {
      const response = await api.post('/api/society-bankbalance', bankBalanceData);
      return response.data;
    } catch (error) {
      console.error('Error creating bank balance record:', error);
      throw error;
    }
  },

  // Update bank balance record
  update: async (id, bankBalanceData) => {
    try {
      const response = await api.put(`/api/society-bankbalance/${id}`, bankBalanceData);
      return response.data;
    } catch (error) {
      console.error('Error updating bank balance record:', error);
      throw error;
    }
  },

  // Get latest bank balance
  getLatestBalance: async () => {
    try {
      const response = await api.get('/api/society-bankbalance/latest/balance');
      return response.data;
    } catch (error) {
      console.error('Error fetching latest bank balance:', error);
      throw error;
    }
  }
};